import { useEffect, useState } from 'react'
import AdvancedTable from 'components/AdvancedTable'
import { Backdrop, Box, Button, CircularProgress, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { Market } from 'types/Market'
import { Can, AbilityContext } from 'config/ability'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { useForm, type FieldValues, type SubmitHandler } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosPost } from 'connectors/axiosPost'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { useAlert } from 'context/AlertContext'
import { axiosDelete } from 'connectors/axiosDelete'
import ConfirmDialog from 'components/ConfirmDialog'
import { useAppContext } from 'context/AppContext/AppContext'
import { useAbility } from '@casl/react'
import { generateQueryParams } from 'utils/generateQueryParams'
import type { Filters } from 'context/FiltersContext/FiltersContext.types'
import { Add } from '@mui/icons-material'
import { SendIcon } from 'assets/icons/send'
import { ExportIcon } from 'assets/icons/export'
import { MarketRow } from './partials/market-row'

export enum MarketPageType {
  'live' = 'live',
  'holding' = 'holding',
  'closed' = 'closed',
  'temporaryClosed' = 'temporaryClosed',
  'liveNoPrizes' = 'liveNoPrizes',
  'empty' = 'empty',
}

enum FilterGroup {
  Markets = 1,
}

export type SelectedValues = {
  marketIds: string[]
} & FieldValues

const environments: Array<{
  code: typeof process.env.REACT_APP_ENVIRONMENT
  url: string
}> = [
  // { code: 'development', url: 'localhost:8000' },
  { code: 'beta', url: process.env.REACT_APP_WEB_BETA ?? '' },
  { code: 'stage', url: process.env.REACT_APP_WEB_STAGE ?? '' },
  { code: 'production', url: process.env.REACT_APP_WEB_PROD ?? '' },
]

const Markets = (): JSX.Element => {
  const queryClient = useQueryClient()
  const [isExportMode, setIsExportMode] = useState<boolean>(false)
  const { token, refreshToken, get2fa } = useAuthContext()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { changeMessage } = useAlert()
  const { handleRefetch, markets } = useAppContext()
  const [relationError, setRelationError] = useState<{
    id: string | null
    msg: string
  }>({ id: null, msg: '' })
  const [dataToMigrate, setDataToMigrate] = useState<{
    marketIds?: Array<string | number>
  } | null>(null)
  const ability = useAbility(AbilityContext)
  const methods = useForm<SelectedValues>({ defaultValues: { marketIds: [] } })

  const handleClearRelationError = (): void => {
    setRelationError({ id: null, msg: '' })
  }

  const handleDownloadMarkets = async (filterToDownload: Filters): Promise<any> => {
    const params = generateQueryParams(filterToDownload)

    const url = `${process.env.REACT_APP_API_URL}/markets/export?${params.toString()}`

    const options = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }

    const res = await fetch(url, options)

    if (res.ok) {
      const blob = await res.blob()
      const file = window.URL.createObjectURL(blob)

      window.location.assign(file)
      setIsExportMode(false)
      void queryClient.refetchQueries(['markets-query'])
    } else {
      const data = await res.json()
      setIsExportMode(false)
      if (data?.message !== undefined) {
        changeMessage(data.message, 'error')
      }
    }
    setIsExportMode(false)
  }

  const {
    mutate: migrateMutate,
    data: migrateResponse,
    reset,
    isLoading: isMutationLoading,
  } = useMutation(
    async ({ marketIds, environment }: { environment?: string; marketIds?: Array<string | number> }) =>
      await axiosPost<string>(token, refreshToken, 'markets/migrate', {
        marketIds,
        environment,
      }),
    {
      onSuccess: async response => {
        if (
          response !== undefined &&
          typeof response !== 'string' &&
          'error' in response &&
          response.error !== undefined
        ) {
          changeMessage(response.error.data.message, 'error', reset)
        } else {
          if (response instanceof Array) {
            response.forEach(el => {
              const marketCode =
                markets !== undefined
                  ? (markets.data.find(market => market.id.toString() === el.marketId)?.code ?? el.marketId)
                  : el.marketId

              changeMessage(
                <div key={el.marketId}>
                  {el.message !== '' ? el.message : t('common.success')}: {marketCode}
                </div>,
                el.message !== '' ? 'error' : 'success',
                reset
              )
            })
          }
          methods.resetField('marketIds')
          reset()
          void queryClient.refetchQueries(['markets-query'])
        }
      },
    }
  )

  const {
    mutate,
    data: deleteResponse,
    reset: resetDelete,
  } = useMutation(
    async ({ id, hard }: { id: string; hard?: boolean }) =>
      await axiosDelete(token, refreshToken, `markets/${hard !== undefined && hard ? 'hard/' : ''}${id}`),
    {
      onSuccess: () => {
        void queryClient.refetchQueries(['markets-query'])
        handleRefetch()
      },
      onSettled: (data, _, { id }) => {
        if (
          data !== undefined &&
          'error' in data &&
          data.error !== undefined &&
          Boolean(data.error.data.message.includes('Relation error'))
        ) {
          setRelationError({ id, msg: data.error.data.message })
        }
      },
    }
  )

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (
      deleteResponse !== undefined &&
      typeof deleteResponse !== 'string' &&
      'error' in deleteResponse &&
      deleteResponse.error !== undefined &&
      !deleteResponse.error?.data.message.includes('Relation error')
    ) {
      changeMessage(deleteResponse?.error?.data?.message, 'error', resetDelete)
    }
  }, [deleteResponse])

  useEffect(() => {
    if (migrateResponse !== undefined && typeof migrateResponse !== 'string') {
      if ('error' in migrateResponse) {
        changeMessage(migrateResponse?.error?.data?.message, 'error', reset)
      }
    }
  }, [migrateResponse])

  const handleMigrate = (environment: string): void => {
    if (dataToMigrate !== null) {
      migrateMutate({ marketIds: dataToMigrate.marketIds, environment })
    }
    setDataToMigrate(null)
  }

  const onSubmit: SubmitHandler<SelectedValues> = data => {
    setDataToMigrate({ marketIds: data.marketIds })
  }

  const changeExportMode = (value: boolean): void => {
    setIsExportMode(value)
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={600}>
            {t('navigation.markets')}
          </Typography>
          <Typography component="p" variant="body1" fontWeight={400} color="grey.600">
            {t('common.trackMarkets')}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Can I="create" a="Markets">
            <Button
              variant="outlined"
              color="secondary"
              onClick={(): void => {
                navigate('/markets/add')
              }}
              startIcon={<Add />}
            >
              {t('common.add')}
            </Button>
          </Can>
          <Can I="migrate" a="Markets">
            <Button variant="outlined" form="markets-table-form" type="submit" color="secondary" startIcon={SendIcon}>
              {t('common.migrate')}
            </Button>
          </Can>
          <Can I="download" a="Markets">
            <Button
              onClick={() => {
                setIsExportMode(true)
              }}
              type="button"
              variant="contained"
              startIcon={ExportIcon}
            >
              {t('common.exportList')}
            </Button>
          </Can>
        </Box>
      </Box>
      <AdvancedTable<Market>
        name="markets"
        endpoint="markets"
        formId="markets-table-form"
        changeExportMode={changeExportMode}
        formHandler={methods.handleSubmit(onSubmit)}
        isExportMode={isExportMode}
        onExport={filters => {
          if (isExportMode) {
            get2fa(
              async tfa => {
                await handleDownloadMarkets({ ...filters, tfa })
              },
              () => {
                setIsExportMode(false)
              }
            )
          }
        }}
        filterGroups={[{ id: FilterGroup.Markets, label: t('markets.marketsFilterGroup') }]}
        headings={[
          {
            type: 'empty',
            name: '',
            field: '',
            width: 74,
            hide: !ability.can('migrate', 'Markets'),
          },
          { type: 'empty', name: 'ID', field: 'id', sort: true, width: 90 },
          {
            type: 'text',
            group: FilterGroup.Markets,
            name: t('market.listingName'),
            field: 'name',
            sort: true,
            width: 180,
          },
          { type: 'empty', group: FilterGroup.Markets, name: t('common.promoFlow'), field: 'type', width: 100 },
          {
            type: 'empty',
            group: FilterGroup.Markets,
            name: t('common.status'),
            field: 'status',
            width: 130,
          },
          {
            type: 'options',
            group: FilterGroup.Markets,
            name: t('common.languages'),
            field: '',
            width: 120,
          },
          { type: 'empty', name: t('market.timezone'), field: '', width: 200 },
          {
            type: 'datetime-range',
            group: FilterGroup.Markets,
            name: t('common.promoLiveDate'),
            field: 'promoLiveDate',
            sort: true,
            width: 190,
          },
          {
            type: 'empty',
            group: FilterGroup.Markets,
            name: t('common.promoEndDate'),
            field: 'promoEndDate',
            sort: true,
            width: 190,
          },
          {
            type: 'datetime-range',
            group: FilterGroup.Markets,
            name: t('common.holdingPageDate'),
            field: 'holdingPageDate',
            sort: true,
            width: 190,
          },
          {
            type: 'empty',
            name: t('common.closedPageEndDate'),
            field: 'closedPageEndDate',
            sort: true,
            width: 190,
          },
          {
            type: 'empty',
            name: t('market.personalDataDeletedBy'),
            field: 'personalDataDeletedBy',
            sort: true,
            width: 190,
          },
          {
            type: 'empty',
            name: t('common.applicationsCount'),
            field: '',
            width: 130,
          },
          {
            type: 'empty',
            name: t('application.emailVerification'),
            field: '',
            width: 140,
          },
          {
            type: 'empty',
            name: t('application.reciptValidation'),
            field: '',
            width: 140,
          },
          {
            type: 'empty',
            name: t('application.accepted'),
            field: '',
            width: 140,
          },
          {
            type: 'empty',
            name: t('application.fraud'),
            field: '',
            width: 140,
          },
          {
            type: 'empty',
            name: t('application.rejected'),
            field: '',
            width: 140,
          },
          {
            type: 'empty',
            name: t('application.prizeDrawWinner'),
            field: '',
            width: 140,
          },
          {
            type: 'empty',
            name: t('application.prizeDrawLoser'),
            field: '',
            width: 140,
          },
        ]}
        options={{
          status: Object.values(MarketPageType).map(el => ({ id: el, value: el })),
          type: [
            { id: 'gwp', value: 'Gift with Purchase' },
            { id: 'pd', value: 'Prize Draw' },
            { id: '', value: 'All' },
          ],
        }}
        renderRow={(row, idx) => (
          <MarketRow
            key={idx}
            {...row}
            idx={idx}
            control={methods.control}
            handleDelete={id => {
              mutate({ id })
            }}
          />
        )}
      />
      <Dialog
        onClose={() => {
          setDataToMigrate(null)
        }}
        open={dataToMigrate?.marketIds !== undefined && dataToMigrate.marketIds.length > 0}
      >
        <DialogTitle>{t('dialogs.wchichEnv')}</DialogTitle>
        <List sx={{ pt: 0 }}>
          {environments.map(environment =>
            process.env.REACT_APP_ENVIRONMENT !== environment.code ? (
              <ListItem key={environment.code} disableGutters>
                <ListItemButton
                  onClick={() => {
                    handleMigrate(environment.url)
                  }}
                >
                  <ListItemText primary={environment.code} secondary={environment.url} />
                </ListItemButton>
              </ListItem>
            ) : null
          )}
        </List>
      </Dialog>
      <ConfirmDialog
        open={relationError.id !== null}
        onAgree={() => {
          if (relationError.id !== null) {
            mutate({ id: relationError.id, hard: true })
            handleClearRelationError()
          }
        }}
        onDisagree={handleClearRelationError}
        question={relationError.msg}
        confirmText={t('common.delete')}
      />
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.modal + 1 }} open={isMutationLoading}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress color="warning" />
          <Typography fontFamily="inherit" fontWeight={800}>
            {t('market.migrating')}
          </Typography>
        </Box>
      </Backdrop>
    </>
  )
}

export default Markets
